import type {
  EditorState,
  Command as PMCommand,
} from '@bangle.io/prosemirror-plugins';
import { ArrowDownAZ, ArrowUpAZ, type LucideIcon } from 'lucide-react';

import type { setupExtensions } from '../extensions';

type EditorExtensions = ReturnType<typeof setupExtensions>;

/**
 * One entry of the editor's right-click menu.
 *
 * `available` is a dry-run of the action's own command, so an item only
 * appears when choosing it would actually change the document. That single
 * gate also decides whether the menu opens at all: right-clicking a paragraph,
 * a heading or a code block runs nothing, so the browser's own menu is left
 * alone there.
 */
export type EditorContextAction = {
  /** Stable kebab-case id. */
  id: string;
  title: () => string;
  icon: LucideIcon;
  available: (ext: EditorExtensions, state: EditorState) => boolean;
  command: (ext: EditorExtensions) => PMCommand;
};

/**
 * Actions offered by the editor's right-click menu. Kept as one registry, like
 * `TABLE_ACTIONS`, so every surface that later offers them gates them the same
 * way instead of re-deciding availability per caller.
 */
export const EDITOR_CONTEXT_ACTIONS: EditorContextAction[] = [
  {
    id: 'sort-asc',
    title: () => t.app.editor.contextMenu.sortAscending,
    icon: ArrowDownAZ,
    available: (ext, state) => ext.list.query.canSortList(state),
    command: (ext) => ext.list.command.sortList('asc'),
  },
  {
    id: 'sort-desc',
    title: () => t.app.editor.contextMenu.sortDescending,
    icon: ArrowUpAZ,
    available: (ext, state) => ext.list.query.canSortList(state),
    command: (ext) => ext.list.command.sortList('desc'),
  },
];

/** The actions that would do something for this editor state. */
export function availableEditorContextActions(
  ext: EditorExtensions,
  state: EditorState,
): EditorContextAction[] {
  return EDITOR_CONTEXT_ACTIONS.filter((action) =>
    action.available(ext, state),
  );
}
